import React from 'react';

import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import EntityFormField from 'src/features/membership/components/forms/EntityFormField';
import { MembershipFormSchemaValues } from 'src/features/membership/formSchema';
import { membershipMessages } from 'src/features/membership/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectTrigger } from 'src/registry/ui/Select';
import { generalMessages } from 'src/registry/utils/messages';
import { Separator } from 'src/registry/ui/Separator';
import { DURATION_UNIT } from 'src/features/membershipType/types';

type MembershipTypeData = {
    name: string;
    duration: number | null;
    durationUnit: DURATION_UNIT | null;
    max_allowed_spaces_count: number | null;
};

type MembershipFormProps = {
    form: UseFormReturn<MembershipFormSchemaValues>;
    onSubmit: (values: MembershipFormSchemaValues) => void;
    membershipTypeData: MembershipTypeData;
};
function MembershipForm({ onSubmit, form, membershipTypeData }: MembershipFormProps) {
    const { formatMessage } = useIntl();

    const validFrom = form.watch('valid_from');

    React.useEffect(() => {
        const { durationUnit, duration } = membershipTypeData;

        if (!durationUnit || !duration || !validFrom) return;

        const newValidTo = dayjs(validFrom).add(duration, durationUnit).toISOString();
        form.setValue('valid_to', newValidTo);
    }, [form, membershipTypeData, validFrom]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormItem variant="dialog">
                <FormLabel variant="dialog" required>
                    {formatMessage(membershipMessages.template)}
                </FormLabel>
                <FormControl>
                    <Select disabled>
                        <SelectTrigger className="w-full">{membershipTypeData.name}</SelectTrigger>
                    </Select>
                </FormControl>
            </FormItem>
            <InputFormField<MembershipFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <Separator />
            <EntityFormField form={form} />
            <Separator />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabled={!!(membershipTypeData.duration && membershipTypeData.durationUnit)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {membershipTypeData.max_allowed_spaces_count && (
                <InputFormField<MembershipFormSchemaValues>
                    form={form}
                    name="max_allowed_spaces_count"
                    label={formatMessage(membershipMessages.maxAllowedSpaces)}
                    type="number"
                    disabled={true}
                    required={false}
                />
            )}
        </Form>
    );
}

export default MembershipForm;
